<script setup>
import { ref, computed } from 'vue';
import axios from 'axios';
import store from '@/store/store';
import {format} from "date-fns";

// Profile data reactive references
const profileData = ref({
  username: store.getters['profile/username'],
  first_name: store.getters['profile/first_name'],
  last_name: store.getters['profile/last_name'],
  email: store.getters['profile/email'],
  phone_number: store.getters['profile/phone_number'],
  date_joined: store.getters['profile/date_joined']
});

// Password change data
const passwordData = ref({
  old_password: '',
  new_password: '',
  confirm_password: ''
});

const isEditable = ref(false);
const isPasswordChangeLoading = ref(false);
const isProfileUpdateLoading = ref(false);

// Computed full name
const fullName = computed(() =>
    `${profileData.value.first_name} ${profileData.value.last_name}`.trim()
);

// Toggle edit mode
const toggleEdit = () => {
  isEditable.value = !isEditable.value;
};

// Update profile method
const updateProfile = async () => {
  isProfileUpdateLoading.value = true;
  try {
    const response = await axios.put("account/", {
      first_name: profileData.value.first_name,
      last_name: profileData.value.last_name,
      email: profileData.value.email,
      phone_number: profileData.value.phone_number,
      username: profileData.value.username,
    });

    const toast = new bootstrap.Toast(document.getElementById('profileUpdateToast'));
    toast.show();
    const account = await axios.get('account/');
    const form = {
      username : account.data.user.username,
      is_superuser : account.data.user.is_superuser,
      first_name : account.data.user.first_name,
      last_name : account.data.user.last_name,
      email : account.data.user.email,
      phone_number : account.data.phone_number,
      is_stuff : account.data.user.is_stuff,
      is_manager : account.data.is_manager,
      is_fitter : account.data.is_fitter,
      abonent : account.data.abonent,
      date_joined : account.data.user.date_joined
    };

    await store.dispatch('profile/setAccount', form);

    console.log(response.data);
  } catch (error) {
    console.error("Error updating profile:", error);

    // Bootstrap error toast
    const errorToast = new bootstrap.Toast(document.getElementById('errorToast'));
    errorToast.show();
  } finally {
    isProfileUpdateLoading.value = false;
  }
};

// Change password method
const changePassword = async () => {
  if (passwordData.value.new_password !== passwordData.value.confirm_password) {
    const errorToast = new bootstrap.Toast(document.getElementById('passwordMatchErrorToast'));
    errorToast.show();
    return;
  }

  isPasswordChangeLoading.value = true;
  try {
    await axios.patch("user/", {
      old_password: passwordData.value.old_password,
      new_password: passwordData.value.new_password
    });

    // Success toast
    const toast = new bootstrap.Toast(document.getElementById('passwordChangeToast'));
    toast.show();

    // Reset password fields
    passwordData.value = {
      old_password: '',
      new_password: '',
      confirm_password: ''
    };
  } catch (error) {
    console.error("Error changing password:", error);

    // Error toast
    const errorToast = new bootstrap.Toast(document.getElementById('errorToast'));
    errorToast.show();
  } finally {
    isPasswordChangeLoading.value = false;
  }
};

const formattedTimestamp = (datetime) => {
  if (!datetime) return ''; // Return empty string if no datetime

  // Try parsing the date
  try {
    const date = new Date(datetime);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return ''; // Return empty string for invalid dates
    }

    return format(date, 'dd-MM-yyyy, HH:mm');
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return ''; // Return empty string if parsing fails
  }
};
</script>

<template>
  <div class="content-wrapper">
    <div class="container py-5">
      <div class="row justify-content-center">
        <div class="col-lg-6 col-md-8">
          <div class="card shadow-sm mt-3">


            <div class="card-body">
              <!-- Profile Info Header -->
              <div class="text-center mb-4">
                <h3>{{ fullName || 'User Profile' }}</h3>
                <p class="text-muted">Дата регистрации: {{ formattedTimestamp(profileData.date_joined) }}</p>
              </div>

              <!-- Edit Toggle Button -->
              <div class="d-grid mb-4">
                <button
                    @click="toggleEdit"
                    class="btn"
                    :class="isEditable ? 'btn-secondary' : 'btn-primary'"
                >
                  {{ isEditable ? 'Заблокировать изменения' : 'Изменить данные' }}
                  <i :class="isEditable ? 'bi bi-lock' : 'bi bi-pencil'" class="ms-2"></i>
                </button>
              </div>

              <!-- Profile Update Form -->
              <form @submit.prevent="updateProfile">
                <div class="row g-3">
                  <div class="col-md-6">
                    <label for="first_name" class="form-label">Имя</label>
                    <input
                        type="text"
                        id="first_name"
                        class="form-control"
                        v-model="profileData.first_name"
                        :disabled="!isEditable"
                    />
                  </div>
                  <div class="col-md-6">
                    <label for="last_name" class="form-label">Фамилия</label>
                    <input
                        type="text"
                        id="last_name"
                        class="form-control"
                        v-model="profileData.last_name"
                        :disabled="!isEditable"
                    />
                  </div>
                  <div class="col-md-12">
                    <label for="username" class="form-label">Имя пользователя</label>
                    <input
                        type="text"
                        id="username"
                        class="form-control"
                        v-model="profileData.username"
                        :disabled="!isEditable"
                    />
                  </div>
                  <div class="col-md-12">
                    <label for="email" class="form-label">Email</label>
                    <input
                        type="email"
                        id="email"
                        class="form-control"
                        v-model="profileData.email"
                        :disabled="!isEditable"
                    />
                  </div>
                  <div class="col-md-12">
                    <label for="phone_number" class="form-label">Номер телефона</label>
                    <input
                        type="text"
                        id="phone_number"
                        class="form-control"
                        v-model="profileData.phone_number"
                        :disabled="!isEditable"
                    />
                  </div>
                  <div class="col-12">
                    <button
                        type="submit"
                        class="btn btn-success w-100"
                        :disabled="!isEditable || isProfileUpdateLoading"
                    >
                    <span
                        v-if="isProfileUpdateLoading"
                        class="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                    ></span>
                      Обновить данные профиля
                    </button>
                  </div>
                </div>
              </form>

              <hr class="my-4">

              <!-- Change Password Section -->
              <h4 class="text-center mb-3">Изменить пароль</h4>
              <form @submit.prevent="changePassword">
                <div class="row g-3">
                  <div class="col-12">
                    <label for="old_password" class="form-label">Действующий пароль</label>
                    <input
                        type="password"
                        id="old_password"
                        class="form-control"
                        v-model="passwordData.old_password"
                        required
                    />
                  </div>
                  <div class="col-12">
                    <label for="new_password" class="form-label">Новый пароль</label>
                    <input
                        type="password"
                        id="new_password"
                        class="form-control"
                        v-model="passwordData.new_password"
                        required
                    />
                  </div>
                  <div class="col-12">
                    <label for="confirm_password" class="form-label">Подтвердите пароль</label>
                    <input
                        type="password"
                        id="confirm_password"
                        class="form-control"
                        v-model="passwordData.confirm_password"
                        required
                    />
                  </div>
                  <div class="col-12">
                    <button
                        type="submit"
                        class="btn btn-primary w-100"
                        :disabled="isPasswordChangeLoading"
                    >
                    <span
                        v-if="isPasswordChangeLoading"
                        class="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                    ></span>
                      Изменить пароль
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <!-- Toast Notifications -->
      <div class="toast-container position-fixed bottom-0 end-0 p-3">
        <div id="profileUpdateToast" class="toast" role="alert">
          <div class="toast-header bg-success text-white">
            <strong class="me-auto">Успех!</strong>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="toast"></button>
          </div>
          <div class="toast-body">Профиль успешно изменён!</div>
        </div>

        <div id="passwordChangeToast" class="toast" role="alert">
          <div class="toast-header bg-success text-white">
            <strong class="me-auto">Успех!</strong>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="toast"></button>
          </div>
          <div class="toast-body">Пароль успешно изменён!</div>
        </div>

        <div id="errorToast" class="toast" role="alert">
          <div class="toast-header bg-danger text-white">
            <strong class="me-auto">Ошибка</strong>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="toast"></button>
          </div>
          <div class="toast-body">Ошибка.</div>
        </div>

        <div id="passwordMatchErrorToast" class="toast" role="alert">
          <div class="toast-header bg-warning text-white">
            <strong class="me-auto">Предупреждение</strong>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="toast"></button>
          </div>
          <div class="toast-body">Пароли не совпадают!</div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.card {
  border-radius: 15px;
}

input:disabled {
  background-color: #e9ecef;
  opacity: 1;
}
</style>